import { useEffect, useState } from "react";
import { Loader2, RotateCw, Send, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { parentApi } from "@/lib/parentApi";
import { useLanguage } from "@/context/LanguageContext";

// 5-star rating picker (interactive). Tap a star to set the rating.
function StarRating({ value, onChange, ariaLabel }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div className="flex gap-1" role="radiogroup" aria-label={ariaLabel}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="p-1"
        >
          <Star
            className={"w-7 h-7 transition " + (n <= display ? "text-burns-gold fill-burns-gold" : "text-zinc-700")}
          />
        </button>
      ))}
    </div>
  );
}

export default function StarReviewForm({ defaultName = "", camps = [] }) {
  const { t } = useLanguage();
  const [campId, setCampId] = useState("");
  const [rating, setRating] = useState(0);
  const [quote, setQuote] = useState("");
  const [name, setName] = useState(defaultName);
  const [role, setRole] = useState("");
  const [sending, setSending] = useState(false);
  const [allReviews, setAllReviews] = useState([]);

  useEffect(() => {
    parentApi.get("/parent/reviews").then((r) => setAllReviews(r.data || [])).catch(() => {});
  }, []);

  // Reviews are write-once. If one exists for the picked camp, the form
  // switches into a read-only summary further below.
  const existingForCamp = campId ? allReviews.find((r) => r.camp_id === campId) : null;
  useEffect(() => {
    setRating(0);
    setQuote("");
    setName(defaultName);
    setRole("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!campId) {
      toast.error(t("parent.pickCampFirst"));
      return;
    }
    if (!rating) {
      toast.error(t("parent.reviewPickStars"));
      return;
    }
    if (!quote.trim()) {
      toast.error(t("parent.reviewWriteText"));
      return;
    }
    setSending(true);
    try {
      const camp = camps.find((c) => c.id === campId);
      await parentApi.post("/parent/review", {
        camp_id: campId,
        camp_name: camp?.name || "",
        rating,
        quote,
        role: role.trim() || undefined,
        display_name: name.trim() || undefined,
      });
      toast.success(t("parent.reviewThanks"));
      // Re-fetch so the form switches into read-only mode for this camp
      try {
        const r = await parentApi.get("/parent/reviews");
        setAllReviews(r.data || []);
      } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSending(false);
    }
  };

  // How many camps still need a review? Used to show a friendly nudge banner
  // above the camp picker when the parent has unfinished business.
  const reviewedIds = new Set(allReviews.map((r) => r.camp_id));
  const pendingCamps = camps.filter((c) => !reviewedIds.has(c.id));
  const pendingCount = pendingCamps.length;

  return (
    <form onSubmit={submit} className="space-y-4">
      {pendingCount > 0 && !campId && (
        <div className="bg-burns-gold/10 border border-burns-gold/40 rounded-md p-3 text-burns-gold text-sm flex items-start gap-2">
          <span className="font-bold">★</span>
          <span>{t("parent.reviewPendingNudge").replace("{n}", pendingCount).replace("{plural}", pendingCount === 1 ? "" : "s")}</span>
        </div>
      )}
      <div>
        <Label className="text-zinc-300 text-sm mb-1 block">{t("parent.reviewCampLabel")}</Label>
        <Select value={campId} onValueChange={setCampId}>
          <SelectTrigger className="bg-zinc-950 border-white/20 text-white" data-testid="review-camp">
            <SelectValue placeholder={t("parent.pickCampPlaceholder")} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/20 text-white">
            {camps.length === 0 ? (
              <SelectItem value="__none" disabled>{t("parent.noCampsToReview")}</SelectItem>
            ) : (
              camps.map((c) => {
                const done = reviewedIds.has(c.id);
                return (
                  <SelectItem key={c.id} value={c.id}>
                    <span className="flex items-center gap-2">
                      {c.name}
                      {!done && <span className="text-burns-gold text-[10px] uppercase">· pending</span>}
                      {done && <span className="text-green-400 text-[10px] uppercase">· done</span>}
                    </span>
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>

      {existingForCamp ? (
        <div className="bg-zinc-950 border border-burns-gold/30 rounded-md p-4 space-y-2">
          <p className="text-burns-gold text-xs flex items-center gap-1">
            <RotateCw className="w-3 h-3" />
            {t("parent.reviewLockedBanner").replace(
              "{date}",
              new Date(existingForCamp.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            )}
            {existingForCamp.is_approved && (
              <span className="ml-1 text-green-400">· {t("parent.reviewApprovedBadge")}</span>
            )}
          </p>
          <div className="flex gap-1">
            {[1,2,3,4,5].map((n) => (
              <Star key={n} className={"w-5 h-5 " + (n <= existingForCamp.rating ? "text-burns-gold fill-burns-gold" : "text-zinc-700")} />
            ))}
          </div>
          <p className="text-zinc-200 text-sm whitespace-pre-wrap">{existingForCamp.quote}</p>
          <p className="text-zinc-500 text-xs">{existingForCamp.name} — {existingForCamp.role}</p>
        </div>
      ) : (
        <>
          <div>
            <Label className="text-zinc-300 mb-2 block">{t("parent.reviewRatingLabel")}</Label>
            <StarRating value={rating} onChange={setRating} ariaLabel={t("parent.reviewRatingLabel")} />
          </div>
          <div>
            <Label className="text-zinc-300 mb-1 block">{t("parent.reviewText")}</Label>
            <Textarea required rows={4} value={quote} onChange={(e) => setQuote(e.target.value)} placeholder={t("parent.reviewTextPlaceholder")} className="bg-zinc-950 border-white/20 text-white" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-sm">{t("parent.reviewName")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={defaultName || t("parent.reviewNamePlaceholder")} className="bg-zinc-950 border-white/20 text-white mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400 text-sm">{t("parent.reviewRole")}</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder={t("parent.reviewRolePlaceholder")} className="bg-zinc-950 border-white/20 text-white mt-1" />
            </div>
          </div>
          <Button
            type="submit"
            disabled={sending || !campId || !rating || !quote.trim() || camps.length === 0}
            className="bg-burns-red hover:bg-burns-red/80 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {t("parent.reviewSubmit")}
          </Button>
          <p className="text-zinc-500 text-xs">{t("parent.reviewHint")}</p>
        </>
      )}
    </form>
  );
}
